import type { CSSProperties } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatDocument, getPeriodLabel } from "@/lib/utils";
import { formatCurrency } from "@/utils/formatters";
import { parseContractContent, resolveRentalContract, groupContractBlocks } from "@/lib/rentalContract";
import "../ContractPrintStyles.css";
interface Equipment {
  type: string;
  gas: string;
  qty: number;
  unitValue?: number;
  serialNumber?: string;
}
interface CompanyData {
  name: string;
  razao_social?: string;
  cnpj?: string;
  domain?: string;
  logo_url?: string;
  endereco?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  telefone?: string;
  email_corporativo?: string;
}
interface CustomerData {
  name: string;
  document_type: 'cpf' | 'cnpj';
  document_number: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  email?: string;
  phone?: string;
}
interface ContractData {
  id: string;
  client: string;
  customer?: CustomerData;
  equipments: Equipment[];
  unitValue?: number;
  totalValue?: number;
  quantity?: number;
  period?: 'monthly' | 'quarterly' | 'semi_annual' | 'annual';
  periodLabel?: string;
  monthlyValue?: number;
  startDate: string;
  nextDueDate?: string;
  company?: CompanyData;
  nfe_number?: string;
  cfop?: string;
}
interface ContractTemplateProps {
  data: ContractData;
  /** L-ASS.0 FRONT-G1: título/texto resolvidos (modelo da empresa ou padrão). Ausentes => padrão. */
  title?: string;
  content?: string;
  /** Faixa "ASSINATURA DIGITAL DISPONÍVEL" só quando houver link de assinatura pendente. */
  signaturePending?: boolean;
  signatureData?: {
    customer_name: string;
    customer_document: string;
    signed_at: string;
    signature_data: string;
  };
}

const fmtDate = (dateStr?: string | null): string => {
  if (!dateStr) return '';
  try {
    return format(new Date(dateStr.substring(0, 10) + 'T12:00:00'), 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return '';
  }
};

const summaryCell: CSSProperties = { padding: '3px 6px', borderBottom: '1px solid #e9ecef', fontSize: '12px', textAlign: 'left' };
const summaryCellRight: CSSProperties = { ...summaryCell, textAlign: 'right' };

export const ContractTemplate = ({
  data,
  title,
  content,
  signaturePending = false,
  signatureData
}: ContractTemplateProps) => {
  const currentDate = format(new Date(), "dd 'de' MMMM 'de' yyyy", {
    locale: ptBR
  });
  const companyName = data.company?.name || data.company?.razao_social || "EMPRESA PRINCIPAL";
  const resolved = resolveRentalContract({ title, content });
  const clauseGroups = groupContractBlocks(parseContractContent(resolved.content));

  // Format company address (dado ausente = linha omitida)
  const getCompanyAddress = () => {
    const company = data.company;
    if (!company) return "";
    const parts = [company.endereco, company.numero, company.bairro, company.cidade, company.estado, company.cep].filter(Boolean);
    return parts.join(", ");
  };

  // Format customer address (dado ausente = linha omitida)
  const getCustomerAddress = () => {
    const customer = data.customer;
    if (!customer) return "";
    const parts = [customer.address, customer.city, customer.state, customer.zip_code].filter(Boolean);
    return parts.join(", ");
  };

  // Format document info (sem substitutos entre colchetes)
  const getCustomerDocumentInfo = () => {
    const customer = data.customer;
    if (!customer?.document_type) return { documentType: "", documentNumber: "" };
    return {
      documentType: customer.document_type === 'cpf' ? 'CPF' : 'CNPJ',
      documentNumber: customer.document_number ? formatDocument(customer.document_number, customer.document_type) : ""
    };
  };
  const documentInfo = getCustomerDocumentInfo();
  const companyAddress = getCompanyAddress();
  const customerAddress = getCustomerAddress();

  // Quadro-resumo: só o que existe
  const equipments = data.equipments ?? [];
  const hasSerial = equipments.some(e => !!e.serialNumber);
  const hasUnitValue = equipments.some(e => (e.unitValue ?? 0) > 0);
  const periodLabel = data.periodLabel ?? (data.period ? getPeriodLabel(data.period) : "");
  const startDate = fmtDate(data.startDate);
  const nextDueDate = fmtDate(data.nextDueDate);
  const hasMonthlyValue = typeof data.monthlyValue === 'number' && data.monthlyValue > 0;
  const showSummary = equipments.length > 0 || hasMonthlyValue || !!periodLabel || !!startDate || !!nextDueDate;

  return <div className="contract-principal">
      {/* Cabeçalho moderno com logo à esquerda */}
      <div className="contract-header-flex">
        {/* Logo à esquerda */}
        <div className="contract-logo-container">
          {data.company?.logo_url && <img src={data.company.logo_url} alt={data.company.name || data.company.razao_social} className="contract-logo-left" />}
        </div>

        {/* Informações da empresa à direita */}
        <div className="contract-company-details">
          <h1 className="contract-title">
            {resolved.title}
          </h1>
          <div className="contract-company-info">
            Contrato Nº: {data.id} | Data: {currentDate}
          </div>
          {data.nfe_number && (
            <div className="contract-company-info">
              Nota Fiscal Nº: {data.nfe_number} | CFOP: {data.cfop || '5908'}
            </div>
          )}
        </div>
      </div>

      {/* Dados das Partes do Contrato - Estilo moderno igual ao recibo */}
      <div className="contract-section contract-avoid-break">
        <h3 className="contract-section-title">LOCADORA:</h3>
        <div className="contract-section-content">
          <div className="contract-section-row">
            <span className="contract-label">Nome/Razão Social:</span>
            <span className="contract-value">{companyName.toUpperCase()}</span>
          </div>
          {data.company?.cnpj && (
            <div className="contract-section-row">
              <span className="contract-label">CNPJ:</span>
              <span className="contract-value">{formatDocument(data.company.cnpj, 'cnpj')}</span>
            </div>
          )}
          {companyAddress && (
            <div className="contract-section-row">
              <span className="contract-label">Endereço:</span>
              <span className="contract-value">{companyAddress}</span>
            </div>
          )}
          <div className="contract-section-row">
            <span className="contract-label">Denominação:</span>
            <span className="contract-value">Doravante denominada simplesmente <strong>Locadora</strong></span>
          </div>
        </div>
      </div>

      <div className="contract-section contract-avoid-break">
        <h3 className="contract-section-title">LOCATÁRIO:</h3>
        <div className="contract-section-content">
          <div className="contract-section-row">
            <span className="contract-label">Nome/Razão Social:</span>
            <span className="contract-value">{(data.customer?.name || data.client).toUpperCase()}</span>
          </div>
          {documentInfo.documentType && documentInfo.documentNumber && (
            <div className="contract-section-row">
              <span className="contract-label">{documentInfo.documentType}:</span>
              <span className="contract-value">{documentInfo.documentNumber}</span>
            </div>
          )}
          {customerAddress && (
            <div className="contract-section-row">
              <span className="contract-label">Endereço:</span>
              <span className="contract-value">{customerAddress}</span>
            </div>
          )}
          <div className="contract-section-row">
            <span className="contract-label">Denominação:</span>
            <span className="contract-value">Doravante denominado simplesmente <strong>Locatário</strong></span>
          </div>
        </div>
      </div>

      {/* Quadro-resumo (L-ASS.0 FRONT-G1) — campo sem dado não aparece */}
      {showSummary && (
        <div className="contract-section contract-avoid-break">
          <h3 className="contract-section-title">QUADRO-RESUMO:</h3>
          <div className="contract-section-content">
            {equipments.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={summaryCell}>Descrição</th>
                    <th style={summaryCellRight}>Qtd</th>
                    {hasSerial && <th style={summaryCell}>Nº registro/série</th>}
                    {hasUnitValue && <th style={summaryCellRight}>Valor unitário</th>}
                  </tr>
                </thead>
                <tbody>
                  {equipments.map((eq, i) => (
                    <tr key={i}>
                      <td style={summaryCell}>{eq.type}{eq.gas ? ` (${eq.gas})` : ''}</td>
                      <td style={summaryCellRight}>{eq.qty}</td>
                      {hasSerial && <td style={summaryCell}>{eq.serialNumber || ''}</td>}
                      {hasUnitValue && <td style={summaryCellRight}>{(eq.unitValue ?? 0) > 0 ? formatCurrency(eq.unitValue) : ''}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {hasMonthlyValue && (
              <div className="contract-section-row">
                <span className="contract-label">Valor mensal:</span>
                <span className="contract-value">{formatCurrency(data.monthlyValue)}</span>
              </div>
            )}
            {periodLabel && (
              <div className="contract-section-row">
                <span className="contract-label">Período:</span>
                <span className="contract-value">{periodLabel}</span>
              </div>
            )}
            {startDate && (
              <div className="contract-section-row">
                <span className="contract-label">Início:</span>
                <span className="contract-value">{startDate}</span>
              </div>
            )}
            {nextDueDate && (
              <div className="contract-section-row">
                <span className="contract-label">Próximo vencimento:</span>
                <span className="contract-value">{nextDueDate}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cláusulas — texto único do contrato (modelo da empresa ou padrão) */}
      {clauseGroups.map((group, gi) => (
        <div key={gi} className={gi === 0 ? "contract-clause contract-page-break" : "contract-clause"}>
          {group.title && <h3 className="contract-clause-title">{group.title}</h3>}
          {group.parts.map((part, pi) =>
            part.kind === 'list' ? (
              <ul key={pi} className="contract-obligations">
                {part.items.map((item, ii) => <li key={ii}>{item}</li>)}
              </ul>
            ) : (
              <p key={pi}>{part.text}</p>
            )
          )}
        </div>
      ))}

      {/* Assinaturas - Sempre na última página */}
      <div className="contract-signatures contract-page-break">
        {signatureData ? (
          // Mostrar assinatura real
          <>
            <div className="contract-section-title">✅ ASSINATURA DIGITAL</div>

            <div className="contract-digital-info" style={{marginBottom: '30px', padding: '15px', background: '#f8f9fa', borderRadius: '8px'}}>
              <p style={{margin: '5px 0'}}><strong>Nome:</strong> {signatureData.customer_name}</p>
              <p style={{margin: '5px 0'}}><strong>CPF:</strong> {signatureData.customer_document}</p>
              <p style={{margin: '5px 0'}}><strong>Data/Hora:</strong> {format(new Date(signatureData.signed_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</p>
            </div>

            <div style={{textAlign: 'center', marginTop: '30px'}}>
              <img
                src={signatureData.signature_data}
                alt="Assinatura Digital"
                style={{maxHeight: '100px', margin: '20px auto', display: 'block', border: '1px solid #ddd', padding: '10px', borderRadius: '4px'}}
              />
            </div>

            <p style={{fontSize: '11px', color: '#666', marginTop: '30px', textAlign: 'center'}}>
              Este documento foi assinado digitalmente e possui validade jurídica.
            </p>
          </>
        ) : (
          // Mostrar placeholder original
          <>
            {signaturePending && (
              <div className="contract-digital-signature-footer contract-avoid-break">
                <div className="contract-section-title">📝 ASSINATURA DIGITAL DISPONÍVEL</div>
                <div className="contract-digital-info">
                  Acesse o link enviado por e-mail para assinar digitalmente este contrato
                </div>
              </div>
            )}

            <div className="contract-signature-area">
              <div className="contract-signature-block"></div>
            </div>

            <div className="contract-date-signature">
              <p>Data: {currentDate}</p>
            </div>
          </>
        )}
      </div>
    </div>;
};
